import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, Loader2, X, AlertTriangle } from 'lucide-react';

const ProcessingModal = ({ 
  isOpen, 
  status, // 'processing' | 'complete' | 'error' | 'cancelled'
  progress, // 0-100
  onClose,
  onViewResults,
  onRetry,
  onCancel, // New: callback to cancel the request
  jobId, // New: current job ID for cancellation
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Smooth progress animation
  useEffect(() => {
    if (status === 'processing' && progress > displayProgress) {
      const interval = setInterval(() => {
        setDisplayProgress(prev => {
          if (prev >= progress) {
            clearInterval(interval);
            return progress;
          }
          return Math.min(prev + 1, progress);
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [progress, status, displayProgress]);

  // Set progress to 100 when complete
  useEffect(() => {
    if (status === 'complete' && displayProgress !== 100) {
      const timeout = setTimeout(() => setDisplayProgress(100), 0);
      return () => clearTimeout(timeout);
    }
  }, [status, displayProgress]);

  // Reset cancel confirm when modal closes/opens - use callback to avoid effect warning
  const resetCancelState = useCallback(() => {
    setShowCancelConfirm(false);
    setCancelling(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Use microtask to avoid synchronous setState in effect
      queueMicrotask(resetCancelState);
    }
  }, [isOpen, resetCancelState]);

  // IMMEDIATE cancel - no confirmation needed
  const handleCancelClick = async () => {
    setCancelling(true);
    if (onCancel) {
      await onCancel();
    }
    setCancelling(false);
  };

  // Legacy function kept for compatibility
  const handleConfirmCancel = async () => {
    setCancelling(true);
    if (onCancel) {
      await onCancel();
    }
    setCancelling(false);
    setShowCancelConfirm(false);
  };

  const handleContinueProcessing = () => {
    setShowCancelConfirm(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-md"
        style={{ 
          backgroundColor: '#FFFFFF',
          border: 'none',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
        }}
        hideClose={true}
      >
        {/* Custom X button for processing state */}
        {status === 'processing' && !showCancelConfirm && (
          <button
            onClick={handleCancelClick}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            style={{ color: '#64748B' }}
            aria-label="Cancel request"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="space-y-6 py-4">
          {/* Cancel Confirmation Dialog */}
          {showCancelConfirm && (
            <>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                  <AlertTriangle className="h-8 w-8" style={{ color: '#F59E0B' }} />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#1E293B' }}>
                  Cancel Request?
                </h2>
                <p className="text-base" style={{ color: '#64748B' }}>
                  Are you sure you want to cancel this request?
                </p>
                <p className="text-sm mt-2" style={{ color: '#EF4444' }}>
                  Progress will be lost and you&apos;ll need to start over.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleConfirmCancel}
                  disabled={cancelling}
                  className="flex-1 h-12 text-base font-semibold"
                  style={{ backgroundColor: '#EF4444', color: '#FFFFFF' }}
                >
                  {cancelling ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Cancel Request'
                  )}
                </Button>
                <Button
                  onClick={handleContinueProcessing}
                  disabled={cancelling}
                  variant="outline"
                  className="flex-1 h-12 text-base font-semibold"
                  style={{ borderColor: '#245451', color: '#245451' }}
                >
                  Continue Processing
                </Button>
              </div>
            </>
          )}

          {/* Processing State */}
          {status === 'processing' && !showCancelConfirm && (
            <>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(36, 84, 81, 0.1)' }}>
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#245451' }} />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#245451' }}>
                  Processing Your Request
                </h2>
                <p className="text-base font-semibold" style={{ color: '#EF4444' }}>
                  Do not leave this page. We are processing your request.
                </p>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: '#64748B' }}>
                    Progress
                  </span>
                  <span className="text-lg font-bold" style={{ color: '#245451' }}>
                    {displayProgress}%
                  </span>
                </div>
                <div className="relative w-full h-4 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                  <div 
                    className="absolute top-0 left-0 h-full transition-all duration-500 ease-out"
                    style={{ 
                      width: `${displayProgress}%`,
                      backgroundColor: '#245451',
                      boxShadow: '0 0 10px rgba(36, 84, 81, 0.5)'
                    }}
                  />
                </div>
                <p className="text-xs text-center mt-2" style={{ color: '#94A3B8' }}>
                  {displayProgress < 30 && 'Uploading documents...'}
                  {displayProgress >= 30 && displayProgress < 60 && 'Extracting text with OCR...'}
                  {displayProgress >= 60 && displayProgress < 90 && 'Analyzing with AI...'}
                  {displayProgress >= 90 && 'Finalizing results...'}
                </p>
              </div>
            </>
          )}

          {/* Complete State */}
          {status === 'complete' && (
            <>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                  <CheckCircle2 className="h-8 w-8" style={{ color: '#22C55E' }} />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#22C55E' }}>
                  Processing Complete
                </h2>
                <p className="text-base" style={{ color: '#64748B' }}>
                  Your results are ready.
                </p>
              </div>

              {/* Progress Bar at 100% */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: '#64748B' }}>
                    Progress
                  </span>
                  <span className="text-lg font-bold" style={{ color: '#22C55E' }}>
                    100%
                  </span>
                </div>
                <div className="relative w-full h-4 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                  <div 
                    className="absolute top-0 left-0 h-full w-full"
                    style={{ 
                      backgroundColor: '#22C55E',
                      boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
                    }}
                  />
                </div>
              </div>

              <Button
                onClick={onViewResults}
                className="w-full h-12 text-base font-semibold"
                style={{ backgroundColor: '#245451', color: '#FFFFFF' }}
              >
                View Results
              </Button>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <AlertCircle className="h-8 w-8" style={{ color: '#EF4444' }} />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#EF4444' }}>
                  Something Went Wrong
                </h2>
                <p className="text-base" style={{ color: '#64748B' }}>
                  We were unable to complete your request. Please try again.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 h-12 text-base"
                  style={{ borderColor: '#E5E7EB', color: '#64748B' }}
                >
                  Close
                </Button>
                <Button
                  onClick={onRetry}
                  className="flex-1 h-12 text-base font-semibold"
                  style={{ backgroundColor: '#245451', color: '#FFFFFF' }}
                >
                  Retry
                </Button>
              </div>
            </>
          )}

          {/* Cancelled State */}
          {status === 'cancelled' && (
            <>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(100, 116, 139, 0.1)' }}>
                  <X className="h-8 w-8" style={{ color: '#64748B' }} />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#64748B' }}>
                  Request Cancelled
                </h2>
                <p className="text-base" style={{ color: '#64748B' }}>
                  Your request has been cancelled. No credits were consumed.
                </p>
              </div>

              <Button
                onClick={onClose}
                className="w-full h-12 text-base font-semibold"
                style={{ backgroundColor: '#245451', color: '#FFFFFF' }}
              >
                Close
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessingModal;
